import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, FormField, Input, Label, PageHeader, Select, useToast } from '@yhelp/ui';
import type { TicketClassification, TicketPriority, TicketProduct } from '@yhelp/shared-types';
import { DashboardLayout } from '@/templates/DashboardLayout';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { TICKET_CLASSIFICATIONS, TICKET_PRODUCTS } from '@/lib/ticket-meta';
import {
  TicketAttachmentField,
  uploadTicketAttachments,
  type PendingAttachment,
} from '@/components/molecules/TicketAttachmentField';
import { ClassificationBadge } from '@/components/molecules/ClassificationBadge';

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'baixo', label: 'Baixo' },
  { value: 'medio', label: 'Médio' },
  { value: 'alto', label: 'Alto' },
  { value: 'critico', label: 'Crítico' },
];

export function CreateTicketPage() {
  const navigate = useNavigate();
  const authorizedFetch = useAuthorizedFetch();
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medio');
  const [product, setProduct] = useState<TicketProduct>('cloudflare');
  const [classification, setClassification] = useState<TicketClassification>('troubleshoot');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

  const create = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch<{ data: { id: string; number: number } }>('/v1/tickets', {
        method: 'POST',
        body: JSON.stringify({ title, description, priority, product, classification }),
      });
      if (attachments.length > 0) {
        await uploadTicketAttachments(res.data.id, attachments, authorizedFetch);
      }
      return res;
    },
    onSuccess: (res) => {
      toast({
        variant: 'success',
        title: 'Chamado criado',
        description: `#${res.data.number} registrado com sucesso.`,
      });
      void navigate({ to: '/tickets/$ticketId', params: { ticketId: res.data.id } });
    },
    onError: () => {
      toast({ variant: 'error', title: 'Erro ao criar chamado', description: 'Tente novamente.' });
    },
  });

  return (
    <DashboardLayout pageDescription="Descreva o problema para abrir um novo chamado">
      <PageHeader title="Abrir chamado" />
      <Card variant="featured" className="mx-auto max-w-2xl p-6" data-tour="ticket-create-form">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <FormField id="title" label="Assunto">
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength={3}
              placeholder="Resumo claro do problema ou solicitação"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <div data-tour="ticket-field-product">
              <Label htmlFor="product">Produto</Label>
              <Select
                id="product"
                className="mt-1"
                value={product}
                onChange={(e) => setProduct(e.target.value as TicketProduct)}
                required
              >
                {TICKET_PRODUCTS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </div>
            <div data-tour="ticket-field-classification">
              <Label htmlFor="classification">Classificação</Label>
              <Select
                id="classification"
                className="mt-1"
                value={classification}
                onChange={(e) => setClassification(e.target.value as TicketClassification)}
                required
              >
                {TICKET_CLASSIFICATIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <div className="mt-2">
                <ClassificationBadge classification={classification} />
              </div>
            </div>
          </div>

          <FormField id="description" label="Descrição">
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={5}
              placeholder="Detalhe o contexto, passos para reproduzir e impacto…"
              className="min-h-[100px] w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-base text-yhelp-black placeholder:text-neutral-400 focus-visible:border-yhelp-yellow focus-visible:outline-none dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            />
          </FormField>

          <div>
            <Label htmlFor="priority">Prioridade</Label>
            <Select
              id="priority"
              className="mt-1"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </div>

          <TicketAttachmentField
            files={attachments}
            onChange={setAttachments}
            disabled={create.isPending}
          />

          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="submit" loading={create.isPending}>
              Abrir chamado
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigate({ to: '/tickets' })}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </DashboardLayout>
  );
}
